import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';

const INITIAL_COUNTS = {
  attendancePending: null,
  leavePending: null,
  taskPending: null,
  wfhPending: null,
  compOffPending: null,
  onboardingPending: null,
  offerLetterPending: null,
  probationPending: null,
  internshipPending: null,
  fullAndFinalPending: null,
};

const toCount = (value) => (typeof value === 'number' ? value : null);

const useAdminSidebarCounts = () => {
  const [counts, setCounts] = useState(INITIAL_COUNTS);
  const controllersRef = useRef({});

  const requestCount = useCallback(async (key, url, dataKey) => {
    if (controllersRef.current[key]) {
      controllersRef.current[key].abort();
    }

    const controller = new AbortController();
    controllersRef.current[key] = controller;

    try {
      const res = await api.get(url, { signal: controller.signal });
      const value = toCount(res?.data?.[dataKey]);
      setCounts((prev) => ({ ...prev, [key]: value }));
    } catch (err) {
      if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
        // Keep this non-blocking so sidebar still renders if an endpoint is temporarily unavailable.
        console.error(`Failed to fetch ${key}`, err);
      }
    }
  }, []);

  const fetchAttendancePending = useCallback(() => {
    requestCount('attendancePending', '/admin/attendance-requests/stats/summary', 'pending');
  }, [requestCount]);

  const fetchLeavePending = useCallback(() => {
    requestCount('leavePending', '/admin/leaves/stats/summary', 'pending');
  }, [requestCount]);

  const fetchTaskPending = useCallback(() => {
    requestCount('taskPending', '/admin/tasks/stats/summary', 'pending');
  }, [requestCount]);

  // Candidates who have submitted their own onboarding form and are waiting on admin review
  const fetchOnboardingPending = useCallback(() => {
    requestCount('onboardingPending', '/onboarding/candidates/stats/summary', 'submitted');
  }, [requestCount]);

  const fetchOfferLetterPending = useCallback(() => {
    requestCount('offerLetterPending', '/admin/offer-letters/stats/summary', 'pending');
  }, [requestCount]);

  const fetchWfhPending = useCallback(() => {
    requestCount('wfhPending', '/admin/wfh/stats/summary', 'pending');
  }, [requestCount]);

  const fetchCompOffPending = useCallback(() => {
    requestCount('compOffPending', '/admin/comp-off/stats/summary', 'pending');
  }, [requestCount]);

  const fetchProbationPending = useCallback(() => {
    requestCount('probationPending', '/admin/probation/stats/summary', 'pending');
  }, [requestCount]);

  // Interns whose internship window has ended and are waiting on HR action
  const fetchInternshipPending = useCallback(() => {
    requestCount('internshipPending', '/admin/internship/stats/summary', 'pending');
  }, [requestCount]);

  const fetchFullAndFinalPending = useCallback(() => {
    requestCount('fullAndFinalPending', '/admin/full-and-final/stats/summary', 'pending');
  }, [requestCount]);

  const refreshAllCounts = useCallback(() => {
    fetchAttendancePending();
    fetchLeavePending();
    fetchTaskPending();
    fetchWfhPending();
    fetchCompOffPending();
    fetchOnboardingPending();
    fetchOfferLetterPending();
    fetchProbationPending();
    fetchInternshipPending();
    fetchFullAndFinalPending();
  }, [fetchAttendancePending, fetchLeavePending, fetchTaskPending, fetchWfhPending, fetchCompOffPending, fetchOnboardingPending, fetchOfferLetterPending, fetchProbationPending, fetchInternshipPending, fetchFullAndFinalPending]);

  useEffect(() => {
    // Fetch once on mount.
    refreshAllCounts();

    // Refresh when the user returns to the tab/window (keeps badges reasonably fresh
    // without continuously polling the backend).
    const handleFocus = () => refreshAllCounts();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshAllCounts();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);

      // eslint-disable-next-line react-hooks/exhaustive-deps
      const controllers = controllersRef.current;
      Object.values(controllers).forEach((controller) => {
        if (controller) {
          controller.abort();
        }
      });
    };
  }, [refreshAllCounts]);

  return {
    counts,
    refreshAllCounts,
    refreshAttendancePending: fetchAttendancePending,
    refreshLeavePending: fetchLeavePending,
    refreshTaskPending: fetchTaskPending,
    refreshWfhPending: fetchWfhPending,
    refreshCompOffPending: fetchCompOffPending,
    refreshOnboardingPending: fetchOnboardingPending,
    refreshOfferLetterPending: fetchOfferLetterPending,
    refreshProbationPending: fetchProbationPending,
    refreshInternshipPending: fetchInternshipPending,
    refreshFullAndFinalPending: fetchFullAndFinalPending,
  };
};

export default useAdminSidebarCounts;
